import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flex, JoyKitThemeProvider, Box, ButtonV2 } from '@withjoy/joykit';
import { FiltersPanel } from './components/FiltersPanel/FiltersPanel';
import { CollapsibleFiltersPanel } from './components/FiltersPanel/CollapsibleFiltersPanel';
import { filterOutIneligibleListings, sortListingsByEligibility } from './listingEligibility';
import { ListingsPanel } from './components/ListingsPanel/ListingsPanel';
import { Map } from './components/Map/Map';
import { ListButton } from './components/Map/components/ListButton';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { useListingFilters } from './hooks/useListingFilters';
import { matchesPrioritizeTags, useListingSort } from './hooks/useListingSort';
import { useTrackCashFundSplitImpression } from './hooks/useTrackCashFundSplitImpression';
import { useFeaturedPlacements } from './hooks/useFeaturedPlacements';
import { isFeaturedPlacementScenario } from './featuredPlacementSlots';
import { FeaturedPlacementTelemetryProvider } from './FeaturedPlacementTelemetryContext';
import type { FeaturedPlacementTelemetryFields } from './FeaturedPlacementTelemetryContext';
import { FeaturedPlacements } from './components/ListingsPanel/components/FeaturedPlacements/FeaturedPlacements.lazy';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useViewportListings } from './hooks/useViewportListings';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { AccommodationsConsoleProps, AccommodationsConsoleState, AccommodationListingDTO } from '@shared/types/Accommodations.types';
import { useAccommodationsConsoleListings } from './hooks/useAccommodationsConsoleListings';
import { AccommodationsConsoleHeader } from './components/Header';
import { TravelCurrencySelector } from '@shared/components/TravelCurrencySelector';
import { CurrencyPickerWithFundNotice } from './components/Header/CurrencyPickerWithFundNotice';
import DeskTopListingsHeader from './components/ListingsPanel/components/DeskTopListingsHeader';
import type { DesktopListingsHeaderExtras } from './components/ListingsPanel/components/DeskTopListingsHeader.types';
import { EmptyState } from './components/EmptyState';
import { ErrorState } from './components/ErrorState';
import { AccommodationsConsoleErrorBoundary } from './components/AccommodationsConsoleErrorBoundary';
import { differenceInCalendarDays } from 'date-fns';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { usePagination } from './hooks/usePagination';
import { DEFAULT_PANEL_WIDTH, LISTINGS_PANEL_COMPACT_DESKTOP_PADDING_Y, LISTINGS_PANEL_DESKTOP_PADDING_Y, ShowMoreButton } from './components/ListingsPanel';
import { MobileSearchComponent } from './components/MobileSearchBar';
import { SingleHotelView } from './components/SingleHotelView/SingleHotelView';
import { BottomSheet } from '@apps/stays/components/BottomSheet';
import { useAccommodationsConsoleHealth, wrapWithAccommodationsConsoleProvider } from './AccommodationsConsole.health';
import type { UseAccommodationsConsoleSearchParamsStateReturn } from './hooks/useAccommodationsConsoleSearchParamsState';
import { useAccommodationsConsoleSearchParamsState } from './hooks/useAccommodationsConsoleSearchParamsState';
import { usePrioritizeTagsFilter, useSeedDefaultPrioritizeTags } from './hooks/useAccommodationsConsoleSearchParamsState/usePrioritizeTags';
import { useMobileSheetDisclosures } from './hooks/useMobileSheetDisclosures';
import { isValidCoordinates } from '@shared/core/places';
import { useMapTelemetry } from './components/Map/Map.telemetry';
import { Map as MapIcon } from '@withjoy/joykit/icons';

import { useMapControls } from './hooks/useMapControls';
import { MapLoader } from './components/Map/MapLoader';
import { useAccommodationRedirectUrl, createUtmParametersForAccommodationsConsolePage } from './hooks/useAccommodationRedirectUrl';
import { MobileTransparentDrawer } from '@shared/components/MobileTransparentDrawer';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { useTranslation } from '@shared/core';
import { useListingsPanelTelemetry } from './components/ListingsPanel/ListingsPanel.telemetry';
import { AccommodationsConsoleLoader } from './components/AccommodationsConsoleLoader/AccommodationsConsoleLoader';
import { ProgressiveLoader } from '@apps/stays/components/ProgressiveLoader';
import { withWindow } from '@shared/utils/withWindow';
import { useAccommodationsConsoleTelemetry } from './AccommodationsConsole.telemetry';
import type { CashFundView } from './AccommodationsConsole.telemetry.types';
import type { LoadStatus } from '@apps/stays/types';
import { useMount } from '@shared/utils/hooks/useMount';
import { AccommodationMapListSync } from './components/AccommodationMapListSync/AccommodationMapListSync';
import { resolveScenarioFlags, resolveDefaultSortOption, isDesktopHeaderEmpty } from './scenarios/scenarioFlags';
import { useIsHoneymoonFundSearch } from '@shared/hooks/useIsHoneymoonFundSearch/useIsHoneymoonFundSearch';
import type { ScenarioFlags } from './scenarios/scenarios.types';
import type { LatLngLiteral } from '@shared/core/google-maps/types';
import { HotelSearchMapLayout, SortOption } from '@shared/constants/accommodations';
import { useSafeUrlParser } from '@shared/hooks/useSafeUrlParser';
import { useAcquisitionAwareUtm } from '@shared/hooks/useAcquisitionAwareUtm';
import { getSelectedRedirectTelemetryName, getSelectedRedirectTravelOfferProvider } from '@shared/utils/extractHotelRedirects';
import { affiliateHandoffTelemetry } from '@shared/utils/formatting/affiliateHandoff';
import { otaTransferredPricingTelemetry } from './components/ListingsPanel/otaTransferredPricing.telemetry';
import { ScrollTopContainer } from '@shared/components/MobileTransparentDrawer/MobileTransparentDrawer.styles';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTravelWhiteLabelCurrency } from '@shared/hooks/useTravelWhiteLabelCurrency';
import { readNavigatorLanguage } from '@shared/utils/getNavigatorLanguage';
import { formatMinorUnitsAsCurrency } from '@shared/utils/currency/formatMinorUnitsAsCurrency';
import { getCashFundCoverage } from '@shared/utils/cashFund/cashFundCoverage';
import { minorToMajorUnits } from '@shared/utils/currency/minorUnits';
import { HotelOfferDiagnosticsProvider } from '@apps/stays/EventAccommodations/routes/shared/hotelOfferDiagnostics/HotelOfferDiagnosticsContext';
import { AccommodationsConsoleMapSkeleton } from './AccommodationsConsoleLayoutSkeleton';

// Owns mobile drawer open/close state. Map content (children) and listings panel
// are passed from the parent and won't re-render when the drawer toggles.
type MobileMapDrawerLayoutProps = Readonly<{
  children: React.ReactNode;
  headerHeight: number;
  listingsPanelContent: React.ReactNode;
  /** Optional drawer sheet background (cash-fund cream); defaults to the drawer's white. */
  drawerBackgroundColor?: string;
  /** The console fills its parent, so the drawer does too; a screen-tall drawer would start above the parent's top. */
  isDrawerSizedToParent: boolean;
}>;

const MobileMapDrawerLayout = React.memo(function MobileMapDrawerLayout({
  children,
  headerHeight,
  listingsPanelContent,
  drawerBackgroundColor,
  isDrawerSizedToParent,
}: MobileMapDrawerLayoutProps) {
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(true);
  const { tq } = useTranslation('accommodationsConsole');

  return (
    <>
      <Content>
        {!isMobileDrawerOpen && (
          <>
            {children}
            <ScrollTopContainer>
              <ButtonV2
                size="md"
                paddingY={4}
                shape="rounded"
                intent="neutral"
                minWidth={pxToRem(164)}
                typographyVariant="button2"
                onClick={() => setIsMobileDrawerOpen(true)}
              >
                {tq`View List`}
              </ButtonV2>
            </ScrollTopContainer>
          </>
        )}
      </Content>
      {isMobileDrawerOpen ? (
        <MobileTransparentDrawer
          scrollTopCta={{ icon: MapIcon, text: tq`Map`, closeOnClick: true }}
          isOpenByDefault={true}
          onOpenChange={setIsMobileDrawerOpen}
          closedHeightPx={0}
          animationType="spring"
          openPositionVh={0}
          openPositionOffsetPx={headerHeight}
          backgroundColor={drawerBackgroundColor}
          showScrollCtaByDefault={true}
          isSizedToParent={isDrawerSizedToParent}
        >
          {listingsPanelContent}
        </MobileTransparentDrawer>
      ) : null}
    </>
  );
});

type RootProps = Readonly<{
  state: AccommodationsConsoleState;
  onChangeLocation: UseAccommodationsConsoleSearchParamsStateReturn['handleOnChangeLocation'];
  onHeaderSearchSubmit: UseAccommodationsConsoleSearchParamsStateReturn['handleHeaderSearchFormSubmit'];
  selectedHotelId?: string | null;
  isInitialLoad: boolean;
  isShowingAllCouplesPicks: boolean;
  scenarioFlags: ScenarioFlags;
  /** Resolved once by the parent, so the sort the URL is seeded with is the sort the list uses. */
  defaultSortOption: SortOption;
  /** Read once by the parent alongside the sort, rather than re-reading the query param here. */
  isHoneymoonFundSearch: boolean;
}> &
  Pick<
    AccommodationsConsoleProps,
    | 'eventHandle'
    | 'eventName'
    | 'onClose'
    | 'scenarioConfig'
    | 'renderListingEntry'
    | 'renderListingsHeader'
    | 'renderListingsBanner'
    | 'renderListingEntrySkeleton'
    | 'renderFiltersHeader'
    | 'onMarkerClick'
    | 'onListingsSettled'
    | 'markerClickFrom'
  >;

const Root = ({
  state,
  eventHandle,
  eventName,
  onClose,
  onChangeLocation,
  onHeaderSearchSubmit,
  selectedHotelId,
  isInitialLoad,
  isShowingAllCouplesPicks,
  renderListingEntry,
  renderListingsHeader,
  renderListingsBanner,
  renderListingEntrySkeleton,
  renderFiltersHeader,
  onMarkerClick,
  onListingsSettled,
  scenarioConfig,
  scenarioFlags,
  defaultSortOption,
  isHoneymoonFundSearch,
  markerClickFrom,
}: RootProps) => {
  const { topBar, mapLayoutMode } = scenarioConfig;

  // Seeded here and nowhere else: the URL is the single source of truth for which tags lead, and
  // this is the one place that writes a page default into it. The request and the sort read it
  // through usePrioritizeTagsQueryParam, so they cannot disagree with what the filter shows.
  useSeedDefaultPrioritizeTags(scenarioConfig.defaultPrioritizeTags);
  const { prioritizeTags, togglePrioritizeTag } = usePrioritizeTagsFilter();
  const startTimeRef = useRef(Date.now());
  const hasFiredResultsLoadedRef = useRef(false);
  // Tracks whether a listing fetch has actually started — prevents premature firing
  // when isCombinedLoading is false because the query hasn't been dispatched yet
  const hasFetchStartedRef = useRef(false);
  const {
    sourceName,
    skipCouplePicks,
    includeCouplesPicks,
    hideEventNameAndNote,
    showAvailableOnlyTooltip,
    showJoyTravelBenefitsFilter,
    shouldFilterOutNonJoyRoomBlocks,
    shouldShowVenueMapPin: shouldShowVenueMapPinForScenario,
    showFundSavingsSort,
  } = scenarioFlags;

  // Arrived from a cash-fund banner in admin registry. The couple is planning a honeymoon, so the
  // wedding venue is noise: its pin comes off and it never enters the map bounds.
  const shouldShowVenueMapPin = shouldShowVenueMapPinForScenario && !isHoneymoonFundSearch;

  const telemetry = useMapTelemetry();
  const { isFilterPanelOpen, onFilterPanelOpen, onFilterPanelClose, isSearchOpen, onSearchOpen, onSearchClose } = useMobileSheetDisclosures();

  const isMobileOrTablet = useIsMobileOrTablet();
  const { onLoadComplete, onLoadFailure } = useAccommodationsConsoleHealth();
  const [hidePanel, togglePanel] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState<AccommodationListingDTO | null>(null);
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const [headerSize, headerRef] = useSizeObserver();
  // Every new search — a new place, new dates, new party size, or "Search this area" — earns a clean
  // slate: its results are shown in full until the guest moves the map again themselves. The radius
  // is part of it because a zoom-only area search changes nothing else.
  const searchIdentity = JSON.stringify([
    state.circularRegionSearch?.latitude,
    state.circularRegionSearch?.longitude,
    state.circularRegionSearch?.radius,
    state.searchDates,
    state.occupancies,
  ]);
  const { mapViewPort, setMapViewPort, reCenterMap, recenterMapViaLocation, setReCenterMap, recenterMapViaMarkers, hasUserMovedMap, markUserMovedMap, resetUserMovedMap } =
    useMapControls({ searchIdentity });

  const isMapOnlyMode = mapLayoutMode === HotelSearchMapLayout.MAP_ONLY;
  const isCompactMapMode = !isMobileOrTablet && mapLayoutMode === HotelSearchMapLayout.COMPACT;

  // A collapsible filter panel moves its toggle and the currency picker to the listings' title row (desktop only).
  const isFilterPanelToggleShown = !!scenarioConfig.isFilterPanelCollapsible && !isCompactMapMode;
  const [isCollapsibleFilterPanelOpen, setIsCollapsibleFilterPanelOpen] = useState(false);
  const toggleCollapsibleFilterPanel = useCallback(() => setIsCollapsibleFilterPanelOpen(isOpen => !isOpen), []);
  const desktopListingsHeader = useMemo<DesktopListingsHeaderExtras>(() => {
    const cashFundNotice = topBar.cashFundNotice;
    return {
      title: scenarioConfig.listingsTitle,
      filtersToggle: isFilterPanelToggleShown ? { isOpen: isCollapsibleFilterPanelOpen, onToggle: toggleCollapsibleFilterPanel } : undefined,
      currencySelector: scenarioConfig.isFilterPanelCollapsible ? (
        cashFundNotice ? (
          <CurrencyPickerWithFundNotice
            notice={cashFundNotice}
            buttonAppearance="pill"
          />
        ) : (
          <TravelCurrencySelector
            isEnabled
            buttonAppearance="pill"
          />
        )
      ) : undefined,
    };
  }, [
    scenarioConfig.listingsTitle,
    scenarioConfig.isFilterPanelCollapsible,
    topBar.cashFundNotice,
    isFilterPanelToggleShown,
    isCollapsibleFilterPanelOpen,
    toggleCollapsibleFilterPanel,
  ]);
  const shouldShowFullMap = isMapExpanded || !isCompactMapMode;

  // If skipCouplePicks is true or we're in compact mode (where map is hidden initially),
  // disable couple picks mode to ensure consistent behavior
  const effectiveIsShowingAllCouplesPicks = skipCouplePicks || isCompactMapMode ? false : isShowingAllCouplesPicks;

  // Stabilize callbacks to prevent unnecessary re-renders in child hooks
  const handleLoadError = useEventCallback((error: unknown) => {
    onLoadFailure(error, { state, filters });
  });

  const handleLoadSuccess = useEventCallback(() => {
    onLoadComplete();
  });

  // Paid-placement A/B test. In scope: the `guest` and `admin` scenarios (see
  // FEATURED_PLACEMENT_SCENARIOS — admin is in it for sponsored INVENTORY only, since the featured
  // unit below renders on the guest path alone). `ota`, `cashFund` and `honeymoon` stay out: no flag
  // read, no exposure, and `maxSponsoredSlots` omitted from their queries so their sponsored
  // placements behave exactly as they do today.
  //
  // DELIBERATELY READ BEFORE THE LISTINGS QUERY, reversing the earlier ordering fix on this branch.
  // The variant is now a QUERY INPUT: it maps to `maxSponsoredSlots`, the number of sponsored slots
  // the search asks the BE to fill (control 0, hero 1, row 4). The request cannot be built without
  // it, so it cannot be resolved from the response.
  //
  // The known cost, accepted by the team rather than overlooked: this records an Amplitude exposure
  // for EVERY in-scope searcher, including the ones whose destination has no sponsored inventory and
  // who therefore see an identical page in all three arms. The previous code read the flag after the
  // listings precisely to avoid that dilution — that is no longer possible, and it is a conscious
  // trade, not a regression back to the old bug. Analysis compensates by filtering to sessions that
  // actually had sponsored inventory (see `sponsoredResultCount` on `destinationSearched`), and has
  // to split guest from admin (see `scenario` on the same event) because those two populations are
  // now mixed.
  //
  // The flag's own default is `control`, so an outage, an unassigned user or an SSR failure all land
  // on the no-sponsored baseline.
  const isFeaturedPlacementScope = isFeaturedPlacementScenario(scenarioConfig.variant);
  const { value: featuredPlacementVariant = 'control' } = useFeatureValue('staysFeaturedPlacementExperiment', { skip: !isFeaturedPlacementScope });

  // The click-attribution pair every hotel click event in this tree reports, resolved HERE and only
  // here. `undefined` outside the experiment scenarios, never `'control'` — see the context module.
  // The tiles read this instead of the flag: a leaf calling `useFeatureValue` would bill one
  // Amplitude exposure per rendered tile.
  const featuredPlacementTelemetry = useMemo<FeaturedPlacementTelemetryFields>(
    () => ({
      featuredPlacementVariant: isFeaturedPlacementScope ? featuredPlacementVariant : undefined,
      scenario: scenarioConfig.variant,
    }),
    [isFeaturedPlacementScope, featuredPlacementVariant, scenarioConfig.variant],
  );

  const {
    cachedListings,
    listings,
    hasAccommodationPlacesByCouple,
    isFetchingListings,
    isFetchingListingPrices,
    isRefreshingListingPrices,
    pricingError,
    isCombinedLoading,
    hasSettledListings,
    hasApiError,
  } = useAccommodationsConsoleListings({
    state,
    variant: effectiveIsShowingAllCouplesPicks ? 'couple' : 'regional',
    skipCouplePicks: skipCouplePicks,
    shouldFilterOutNonJoyRoomBlocks,
    eventHandle,
    onError: handleLoadError,
    onSuccess: handleLoadSuccess,
    isInitialLoad,
    cashFund: scenarioConfig.cashFund,
    // Undefined outside the in-scope scenarios, which omits `maxSponsoredSlots` entirely rather than
    // sending 0 — see featuredPlacementSlots.ts for why those two are not the same request.
    featuredPlacementVariant: isFeaturedPlacementScope ? featuredPlacementVariant : undefined,
    scenario: scenarioConfig.variant,
    defaultPrioritizeTags: scenarioConfig.defaultPrioritizeTags,
  });

  const {
    filters,
    resetFilters,
    resetFiltersFromEmptyState,
    updateFilters,
    facilityCounts,
    starRatingCounts,
    guestRatingCounts,
    propertyCategoryCounts,
    brandCounts,
    propertyNameSearch,
    filteredProperties,
    supportedFacilities,
    supportedBrands,
    setPropertyNameSearch,
    dynamicPriceMax,
    listingPrices,
  } = useListingFilters(
    listings,
    isFetchingListingPrices,
    defaultSortOption,
    // The scenario's prices also arrive batch by batch, so the slider's ceiling holds while they do.
    isRefreshingListingPrices || !!scenarioConfig.isListingNightlyPricePartial,
    state.venue.location,
    scenarioConfig.getListingNightlyPrice,
    scenarioConfig.getListingEligibility,
  );

  // Read at call time, so a date change adds no call of its own: the call keeps today's triggers. A
  // call that another trigger fires while a new search starts can still pair the new dates with the
  // old listings for one render; the next call carries the listings that search returns.
  const getSettledSearchDates = useEventCallback(() => ({ checkin: state.searchDates.checkin, checkout: state.searchDates.checkout }));

  // Notify parent when listings settle (including empty results)
  useEffect(() => {
    if (isCombinedLoading || !onListingsSettled) return;
    const venue = state.circularRegionSearch;
    onListingsSettled(listings, venue ? { latitude: venue.latitude, longitude: venue.longitude } : null, getSettledSearchDates());
  }, [isCombinedLoading, listings, state.circularRegionSearch, onListingsSettled, getSettledSearchDates]);

  const { onAccommodationsResultsLoaded } = useAccommodationsConsoleTelemetry();
  const stableOnAccommodationsResultsLoaded = useEventCallback(onAccommodationsResultsLoaded);

  // Track when a fetch actually starts so we don't fire before queries are dispatched
  useEffect(() => {
    if (isFetchingListings || isCombinedLoading) {
      hasFetchStartedRef.current = true;
    }
  }, [isFetchingListings, isCombinedLoading]);

  // Fire once when listings settle — measures user-perceived wait time from component mount
  useEffect(() => {
    if (hasFiredResultsLoadedRef.current || isCombinedLoading || !hasFetchStartedRef.current) return;

    let load_status: LoadStatus;
    // Check data presence first — cached listings may be visible even if the fresh fetch errored
    if (listings.length > 0) {
      load_status = 'success';
    } else if (hasApiError) {
      load_status = 'error';
    } else {
      load_status = 'empty';
    }

    // Which fund treatment actually rendered. `cashFund` is set only when the fund applies at this
    // page's currency; the notice alone means a fund exists that cannot be used here. Reported so
    // "assigned to treatment" and "actually saw it" can be told apart — they differ, because the
    // variant also needs a balance and a currency match.
    const cashFundNotice = scenarioConfig.topBar.cashFundNotice;
    // Ordered by how much each one says: a live fund, a fund the currency blocks, a honeymoon
    // search with no fund, then everything else.
    const cashFundView: CashFundView = scenarioConfig.cashFund
      ? 'eligible'
      : cashFundNotice
        ? 'currency_mismatch'
        : scenarioConfig.variant === 'honeymoonAdmin'
          ? 'no_fund'
          : 'none';

    // Reported as a real amount, matching every other cash-fund event. Replaces the eligible-listing
    // count, which read 0 because prices have not attached to the listings when this fires.
    const cashFundBalanceTelemetry = scenarioConfig.cashFund
      ? {
          cashFundBalance: minorToMajorUnits(scenarioConfig.cashFund.balanceMinorUnits, scenarioConfig.cashFund.currency),
          cashFundCurrency: scenarioConfig.cashFund.currency,
        }
      : {};

    stableOnAccommodationsResultsLoaded({
      accommodations_results_loaded_ms: Math.round(Date.now() - startTimeRef.current),
      load_status,
      cash_fund_view: cashFundView,
      cashFundBalance: cashFundBalanceTelemetry,
      scenario: scenarioConfig.variant,
    });
    hasFiredResultsLoadedRef.current = true;
  }, [
    isCombinedLoading,
    isFetchingListings,
    hasApiError,
    listings,
    scenarioConfig.cashFund,
    scenarioConfig.variant,
    scenarioConfig.topBar.cashFundNotice,
    stableOnAccommodationsResultsLoaded,
  ]);

  // Undefined outside the cash-fund variant, which switches off everything below.
  const cashFund = scenarioConfig.cashFund;

  // "Save more, travel further" toggle: keep only stays the fund fully covers (out-of-pocket 0).
  const fundCoveredProperties = useMemo(() => {
    if (!filters.cashFundFullyCoveredOnly || !cashFund) return filteredProperties;
    // Deliberately strict: a part-covered stay still owes the card, so it is not "fully covered".
    // Coverage is null for anything the fund can't pay, so those drop out by rule too.
    return filteredProperties.filter(listing => getCashFundCoverage(listing, cashFund)?.outOfPocketMinorUnits === 0);
  }, [filteredProperties, filters.cashFundFullyCoveredOnly, cashFund]);

  // Undefined when the scenario does not answer eligibility. The eligibility filter and the eligibility order are then both off.
  const getListingEligibility = scenarioConfig.getListingEligibility;

  // Eligibility toggle: drop the listings the scenario answers `false` for. Listings not known yet stay.
  const eligibleProperties = useMemo(() => {
    if (!filters.showEligibleOnly || !getListingEligibility) {
      return fundCoveredProperties;
    }
    return filterOutIneligibleListings(fundCoveredProperties, getListingEligibility);
  }, [fundCoveredProperties, filters.showEligibleOnly, getListingEligibility]);

  // Filter hotels as per map visible port, but only once the guest has moved the map themselves.
  const { listings: listingsInViewport, isShowingOutsideViewport } = useViewportListings({
    listings: eligibleProperties,
    mapViewPort,
    isViewportFilterActive: shouldShowFullMap && hasUserMovedMap && !isFetchingListings && hasSettledListings,
  });

  // Distance sort anchors on the user's searched location so the ordering matches the
  // distance shown on each card. Falls back to the event venue when no location was searched.
  const distanceSortAnchor = useMemo(() => {
    if (state.circularRegionSearch) {
      return {
        name: state.searchLocation ?? state.venue.name,
        location: { lat: state.circularRegionSearch.latitude, lng: state.circularRegionSearch.longitude },
      };
    }
    return state.venue;
  }, [state.circularRegionSearch, state.searchLocation, state.venue]);

  const noOfNights = useMemo(
    () => differenceInCalendarDays(new Date(state.searchDates.checkout), new Date(state.searchDates.checkin)),
    [state.searchDates.checkin, state.searchDates.checkout],
  );

  // Apply sorting based on selected sort option
  const { sortedListings, sortOption, updateSort } = useListingSort({
    filters,
    eventHandle,
    defaultSortOption,
    listings: listingsInViewport,
    sortAnchor: distanceSortAnchor,
    cashFund,
    noOfNights,
    unitCount: state.occupancies.unitCount ?? undefined,
    getListingNightlyPrice: scenarioConfig.getListingNightlyPrice,
  });
  // The scenario's eligible listings first, then those not known yet, then its ineligible ones. Runs after the
  // sort, so the chosen sort still holds inside each group. Runs before pagination, so page one shows the
  // eligible listings first.
  const listingsInEligibilityOrder = useMemo(() => {
    if (!getListingEligibility) {
      return sortedListings;
    }
    return sortListingsByEligibility(sortedListings, getListingEligibility);
  }, [sortedListings, getListingEligibility]);
  // Reads the rendered list, so it reports what the couple actually saw — full cover or a real split.
  useTrackCashFundSplitImpression({ listings: listingsInEligibilityOrder, cashFund });

  // Pinned placements are computed from `listings` — the PRE-filter, PRE-viewport set — so they
  // survive a filter change, a sort change and the desktop map-viewport re-sort. Splitting after
  // pagination would make the band's composition change page by page.
  // Under a tag request the server already returns tagged hotels only, so this is a guard for the
  // window before that deploys and for listings merged in from elsewhere: a sponsored placement is
  // picked from this set, and an untagged ad above a "Show only" page would read as the filter
  // being bypassed. Memoized so the reference is stable and useFeaturedPlacements' own memo holds.
  const placementCandidates = useMemo(
    () => (prioritizeTags.length ? listings.filter(listing => matchesPrioritizeTags(listing, prioritizeTags)) : listings),
    [listings, prioritizeTags],
  );
  // The eligibility toggle hides ineligible listings from the featured placements too, not only from the organic list.
  const eligiblePlacementCandidates = useMemo(() => {
    if (!filters.showEligibleOnly || !getListingEligibility) {
      return placementCandidates;
    }
    return filterOutIneligibleListings(placementCandidates, getListingEligibility);
  }, [placementCandidates, filters.showEligibleOnly, getListingEligibility]);
  const {
    featuredListings,
    searchedListing,
    organicListings,
    insertAfterIndex: featuredInsertAfterIndex,
  } = useFeaturedPlacements({
    allListings: eligiblePlacementCandidates,
    displayedListings: listingsInEligibilityOrder,
    // `control` outside the experiment's scenarios, which makes this a passthrough: nothing is split
    // out and no featured unit renders. `FeaturedPlacements` has no scenario gate of its own, so a
    // variant reaching it is what decides a treatment is drawn — keep that decision here.
    variant: isFeaturedPlacementScope ? featuredPlacementVariant : 'control',
  });

  const shouldUseCouplePickMapMode = effectiveIsShowingAllCouplesPicks && hasAccommodationPlacesByCouple;

  const handleSearchFormSubmit = useEventCallback<UseAccommodationsConsoleSearchParamsStateReturn['handleHeaderSearchFormSubmit']>(value => {
    onHeaderSearchSubmit(value);
    resetFilters();
    recenterMapViaLocation(true);
  });

  /**
   * Generates map markers for the accommodation map to show the couple picks initially.
   * When showing all couples picks, includes:
   * - Markers for all hotels marked as picked by the couple
   * - The search center location marker (if a circular region search is active)
   * Returns an empty array when not showing couples picks.
   */
  const mapMarkers = useMemo(() => {
    if (!shouldUseCouplePickMapMode) {
      return [];
    }

    // Collect markers for all hotels picked by the couple
    const couplePickMarkers: ReadonlyArray<LatLngLiteral> = filteredProperties.reduce<ReadonlyArray<LatLngLiteral>>(
      (markers, hotel) => (hotel.isPickedByCouple ? [...markers, { lat: hotel.location.lat, lng: hotel.location.lng }] : markers),
      [] as ReadonlyArray<LatLngLiteral>,
    );

    // Add the search center location marker if available
    const searchCenter: LatLngLiteral | null = state.circularRegionSearch ? { lat: state.circularRegionSearch.latitude, lng: state.circularRegionSearch.longitude } : null;
    const searchCenterMarker: ReadonlyArray<LatLngLiteral> = searchCenter ? [searchCenter] : ([] as ReadonlyArray<LatLngLiteral>);

    // A honeymoon search zooms to the honeymoon place alone — the couple's picks are wedding-guest
    // hotels by the venue, and fitting them would pull the map back across two countries. Every
    // other search keeps the bounds it has today.
    const picksForViewport = isHoneymoonFundSearch ? [] : couplePickMarkers;

    return [...picksForViewport, ...searchCenterMarker] as const;
  }, [filteredProperties, shouldUseCouplePickMapMode, state.circularRegionSearch, isHoneymoonFundSearch]);

  useEffect(() => {
    if (shouldUseCouplePickMapMode) {
      recenterMapViaMarkers();
    } else {
      recenterMapViaLocation();
    }
  }, [shouldUseCouplePickMapMode, recenterMapViaMarkers, recenterMapViaLocation]);

  const onChangeMapViewPort = useEventCallback((center: { lat: number; lng: number }, bounds: google.maps.LatLngBounds) => {
    setMapViewPort({ center, bounds });
  });

  const getRedirectUrl = useAccommodationRedirectUrl(state, sourceName);
  const listingTelemetry = useListingsPanelTelemetry();

  const parseUrl = useSafeUrlParser();
  // Same resolution the redirect URL uses, so the Amplitude event and the URL can't disagree.
  const mergedUtm = useAcquisitionAwareUtm(createUtmParametersForAccommodationsConsolePage(sourceName));

  // Defer to rAF to avoid forced reflow from interleaving layout reads with writes
  const scrollToSelectedHotel = useEventCallback((hotelId: Maybe<string>) => {
    requestAnimationFrame(() => {
      const hotelElement = document.getElementById(`listing-${hotelId}`);
      hotelElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  const onClickMarker = useEventCallback((hotel: AccommodationListingDTO) => {
    telemetry.mapMarkerClicked({
      listingType: hotel.type,
      placeId: hotel.joyPlaceId,
      hotelName: hotel.name,
      from: markerClickFrom ?? 'fullMap',
    });
    onMarkerClick?.(hotel);
    setSelectedHotel(hotel);
  });

  const handleOnTogglePanel = useEventCallback(() => {
    togglePanel(isOpen => !isOpen);
  });

  const handleResetFilters = useEventCallback(() => {
    resetFiltersFromEmptyState();
    setPropertyNameSearch('');
    // Back to whatever this search opened on, so a honeymoon search that fell back to the plain
    // console does not reset onto couples picks — wedding-guest hotels by the venue.
    // admin/ota keep their long-standing couples-picks reset; changing that is a separate call.
    const keepsLegacyCouplesPicksReset = scenarioConfig.variant === 'admin' || scenarioConfig.variant === 'ota';
    updateSort(keepsLegacyCouplesPicksReset ? SortOption.COUPLES_PICKS : defaultSortOption);
    recenterMapViaLocation();
    resetUserMovedMap();
  });

  // Pagination applies to the organic list only — the featured unit is pinned above/inside it and is
  // never paged.
  const { paginatedListings, showNextPageOfListings, hasMoreListingsToShow, listingsContainerRef } = usePagination({
    listings: organicListings,
    filters,
    sortOption,
    isRefreshingListingPrices,
  });

  // Everything that EXISTS for this search after the placement split — featured plus the whole
  // organic list, before pagination. Deep links (`?hotel=<id>`) resolve against this: a hotel on
  // page three is still a hotel this search found, and looking it up in the rendered page instead
  // would silently fail to open the sheet.
  const allPlacedListings = useMemo(
    () => [...(searchedListing ? [searchedListing] : []), ...featuredListings, ...organicListings],
    [searchedListing, featuredListings, organicListings],
  );

  // Everything CURRENTLY RENDERED: featured plus the current page of organic results, with the
  // featured block at the same position the listings panel paints it. The map's markers and the
  // mobile bottom sheet read this, so swipe order follows the visual order rather than visiting
  // every paid placement first. A hotel suppressed by the `hero` arm is absent here, as intended.
  // The `row` arm's searched-hotel hero, when there is one, is painted above everything else.
  const renderedListings = useMemo(() => {
    // An empty organic page is an empty page, ads or not — the panel shows the EmptyState and paints
    // neither featured unit, so the map and sheet must not carry them either.
    if (paginatedListings.length === 0) return paginatedListings;
    const searched = searchedListing ? [searchedListing] : [];
    if (featuredListings.length === 0) return [...searched, ...paginatedListings];
    // Same clamp `ListingsPanel` applies, so a short page keeps the two in step.
    const insertIndex = Math.min(featuredInsertAfterIndex, paginatedListings.length);
    return [...searched, ...paginatedListings.slice(0, insertIndex), ...featuredListings, ...paginatedListings.slice(insertIndex)];
  }, [searchedListing, featuredListings, paginatedListings, featuredInsertAfterIndex]);

  const openHotelRedirectUrl = useEventCallback((hotel: AccommodationListingDTO) => {
    const redirectUrl = getRedirectUrl(hotel);
    if (!redirectUrl) return;
    listingTelemetry.seeAvailabilityClicked({
      joyPlaceId: hotel?.joyPlaceId || '',
      hotelName: hotel?.name || '',
      isSponsored: hotel.isSponsored ?? false,
      isDirectBooking: hotel.isDirectBooking ?? false,
      chain: hotel.chain,
      isCoupleFavourite: hotel.isFavorite ?? false,
      priceSource: hotel.pricingInfo.source,
      travelOfferProvider: getSelectedRedirectTravelOfferProvider(hotel.redirectMechanisms),
      ...otaTransferredPricingTelemetry(hotel),
      ...featuredPlacementTelemetry,
    });
    const urlInfo = parseUrl(redirectUrl, 'AccommodationsConsole.handleHotelSelection');
    if (urlInfo) {
      listingTelemetry.hotelBookingRedirect({
        joyPlaceId: hotel?.joyPlaceId || '',
        hotelName: hotel?.name || '',
        destinationURL: redirectUrl,
        destinationDomain: urlInfo.hostname,
        destinationName: getSelectedRedirectTelemetryName(hotel.redirectMechanisms),
        isSponsored: hotel.isSponsored ?? false,
        isDirectBooking: hotel.isDirectBooking ?? false,
        chain: hotel.chain,
        isCoupleFavourite: hotel.isFavorite ?? false,
        // Map-marker opens, which bypass every tile.
        ...affiliateHandoffTelemetry(redirectUrl),
        ...otaTransferredPricingTelemetry(hotel),
        ...mergedUtm,
        ...featuredPlacementTelemetry,
      });
    }
    withWindow(global => {
      global.open(redirectUrl, '_blank');
    });
  });

  const handleHotelSelection = useEventCallback(
    (hotel: AccommodationListingDTO) => {
      // When onMarkerClick is provided, the caller owns the action (e.g. ExploreArea opens a drawer)
      if (onMarkerClick) return;
      // In MAP_ONLY mode without onMarkerClick, open the booking URL (works on both mobile and desktop)
      if (isMapOnlyMode) {
        openHotelRedirectUrl(hotel);
        return;
      }
      // On mobile, SingleHotelView opens automatically via the selectedHotel prop
      if (isMobileOrTablet) return;

      if (hidePanel) {
        openHotelRedirectUrl(hotel);
      } else {
        scrollToSelectedHotel(hotel.joyPlaceId);
      }
    },
    [isMobileOrTablet, scrollToSelectedHotel, openHotelRedirectUrl, hidePanel, isMapOnlyMode, onMarkerClick],
  );

  useEffect(() => {
    if (selectedHotel) {
      handleHotelSelection(selectedHotel);
      if (!isMobileOrTablet) {
        setSelectedHotel(null);
      }
    }
  }, [handleHotelSelection, isMobileOrTablet, selectedHotel]);

  // Select a deep-linked hotel once per id. `allPlacedListings` changes on every price batch and filter
  // change; on desktop `selectedHotel` is nulled right after it is handled, so without this ref each
  // change would re-run `handleHotelSelection` — re-scrolling, or reopening the redirect when the panel
  // is hidden.
  const handledSelectedHotelIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedHotelId) {
      handledSelectedHotelIdRef.current = null;
      return;
    }
    if (handledSelectedHotelIdRef.current === selectedHotelId) return;
    const hotel = allPlacedListings.find((h: AccommodationListingDTO) => h.joyPlaceId === selectedHotelId);
    if (!hotel) return;
    handledSelectedHotelIdRef.current = selectedHotelId;
    setSelectedHotel(current => (current?.joyPlaceId === hotel.joyPlaceId ? current : hotel));
  }, [selectedHotelId, allPlacedListings]);

  const handleShowOnMap = useEventCallback(() => {
    setIsMapExpanded(true);
  });

  // Functional setState removes the deps on hidePanel and togglePanel (Vercel 5.9)
  const handleShowList = useCallback(() => {
    setIsMapExpanded(false);
    togglePanel(() => false);
  }, []);

  // Reset map zoom to default when map is expanded from compact mode
  // This ensures the zoom is set even if auto-zoom happens after initial render
  useEffect(() => {
    if (isMapExpanded && isCompactMapMode) {
      // Use a delay to ensure the map is fully rendered and any auto-zoom has completed
      // before resetting to default zoom again
      const timer = setTimeout(() => {
        recenterMapViaLocation(true);
      }, 600);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isMapExpanded, isCompactMapMode, recenterMapViaLocation]);

  const mapLocation = useMemo(
    () =>
      state.circularRegionSearch && isValidCoordinates({ latitude: state.circularRegionSearch.latitude, longitude: state.circularRegionSearch.longitude })
        ? { latlng: { lat: state.circularRegionSearch.latitude, lng: state.circularRegionSearch.longitude } }
        : null,
    [state.circularRegionSearch],
  );

  const searchLocationForMap = useMemo(
    () => (state.searchLocation && mapLocation ? { name: state.searchLocation, location: { lat: mapLocation.latlng.lat, lng: mapLocation.latlng.lng } } : undefined),
    [state.searchLocation, mapLocation],
  );

  const { currency: currencyCode } = useTravelWhiteLabelCurrency();
  // Navigator language is stable for the lifetime of the page, so compute it once
  const navigatorLanguage = useMemo(() => readNavigatorLanguage(), []);

  // A scenario with its own nightly price shows that price, the one its card shows, and an empty label (a pin
  // with no price) where its card shows none — never the search's base rate.
  // Cash-fund variant: markers show the server's out-of-pocket, the same figure the tile quotes.
  // Reads "+$0" on a fully covered stay. Undefined for listings the fund can't pay, which keep the
  // default price label.
  const markerPriceLabelFor = useEventCallback((hotel: AccommodationListingDTO): string | undefined => {
    if (scenarioConfig.getListingNightlyPrice) return scenarioConfig.getListingNightlyPrice(hotel)?.label ?? '';
    if (!cashFund) return undefined;
    const coverage = getCashFundCoverage(hotel, cashFund);
    if (!coverage) return undefined;
    return `+${formatMinorUnitsAsCurrency(coverage.outOfPocketMinorUnits, cashFund.currency, navigatorLanguage)}`;
  });

  const filtersPanelContent = useMemo(
    () => (
      <SkeletonGroup
        isReady={true}
        placeholder={<FiltersPanel.Skeleton showJoyTravelBenefitsFilter={showJoyTravelBenefitsFilter} />}
      >
        <FiltersPanel
          filters={filters}
          eventName={eventName}
          accentColor={scenarioConfig.accentColor}
          onFiltersChange={updateFilters}
          supportedFacilities={supportedFacilities}
          supportedBrands={supportedBrands}
          facilityCounts={facilityCounts}
          brandCounts={brandCounts}
          starRatingCounts={starRatingCounts}
          prices={listingPrices}
          guestRatingCounts={guestRatingCounts}
          propertyCategoryCounts={propertyCategoryCounts}
          isLoadingPrices={isFetchingListingPrices}
          hidePriceRangeFacet={!!pricingError}
          availablePrioritizeTags={scenarioConfig.availablePrioritizeTags ?? scenarioConfig.defaultPrioritizeTags}
          selectedPrioritizeTags={prioritizeTags}
          onPrioritizeTagToggle={togglePrioritizeTag}
          onClose={onFilterPanelClose}
          onReset={handleResetFilters}
          sortOption={sortOption}
          onSortChange={updateSort}
          currencyCode={currencyCode}
          navigatorLanguage={navigatorLanguage}
          dynamicPriceMax={dynamicPriceMax}
          mapData={
            isCompactMapMode && mapLocation
              ? {
                  location: mapLocation,
                  onShowOnMap: handleShowOnMap,
                  shouldShowFullMap: shouldShowFullMap,
                }
              : undefined
          }
          propertyNameSearch={propertyNameSearch}
          onPropertyNameSearchChange={setPropertyNameSearch}
          hideEventNameAndNote={hideEventNameAndNote}
          showAvailableOnlyTooltip={showAvailableOnlyTooltip}
          showJoyTravelBenefitsFilter={showJoyTravelBenefitsFilter}
          includeCouplesPicks={includeCouplesPicks}
          isPanelVisible={!isMobileOrTablet || isFilterPanelOpen}
          renderExtraFilter={renderFiltersHeader}
          showFundSavingsSort={showFundSavingsSort}
          visibleFilters={scenarioConfig.visibleFilters}
          hasVenueLocation={state.venue.location !== null}
        />
      </SkeletonGroup>
    ),
    [
      filters,
      eventName,
      updateFilters,
      supportedFacilities,
      supportedBrands,
      facilityCounts,
      brandCounts,
      starRatingCounts,
      listingPrices,
      guestRatingCounts,
      propertyCategoryCounts,
      isFetchingListingPrices,
      pricingError,
      prioritizeTags,
      togglePrioritizeTag,
      scenarioConfig.defaultPrioritizeTags,
      scenarioConfig.availablePrioritizeTags,
      onFilterPanelClose,
      handleResetFilters,
      sortOption,
      updateSort,
      currencyCode,
      navigatorLanguage,
      dynamicPriceMax,
      isCompactMapMode,
      mapLocation,
      handleShowOnMap,
      shouldShowFullMap,
      propertyNameSearch,
      setPropertyNameSearch,
      hideEventNameAndNote,
      showAvailableOnlyTooltip,
      showJoyTravelBenefitsFilter,
      includeCouplesPicks,
      isMobileOrTablet,
      isFilterPanelOpen,
      renderFiltersHeader,
      showFundSavingsSort,
      scenarioConfig.accentColor,
      scenarioConfig.visibleFilters,
      state.venue.location,
    ],
  );

  const listingsPanelContent = useMemo(
    () => (
      <SkeletonGroup
        isReady={!isFetchingListings}
        overflowY={{ _: undefined, md: 'auto' }}
        width={{ _: '100%', md: hidePanel ? 0 : 'fit-content' }}
        paddingBottom={9}
        placeholder={
          <ListingsPanel.Skeleton
            isMobileOrTablet={isMobileOrTablet}
            listingsPanelWidth={scenarioConfig.listingsPanelWidth}
            renderListingEntrySkeleton={renderListingEntrySkeleton}
            hasCompactPadding={scenarioConfig.isFilterPanelCollapsible}
          />
        }
        rootRef={listingsContainerRef}
      >
        {renderListingsHeader?.()}
        {/* Deliberately ignores `featuredListings`: pinned placements survive a filter, so a tight
            filter can empty the organic list while leaving them in hand. Rendering the paid unit
            over blank space and swallowing the EmptyState would leave the guest with no explanation
            of why nothing else matched — so an empty organic list is an empty page, ads or not. */}
        {paginatedListings.length > 0 || hasApiError ? (
          <>
            <ListingsPanel
              state={state}
              sortOption={sortOption}
              onSortChange={updateSort}
              noOfNights={noOfNights}
              filters={filters}
              updateFilters={updateFilters}
              listings={paginatedListings}
              isLoadingPrices={isFetchingListingPrices}
              hidePanel={hidePanel}
              hasError={hasApiError}
              sourceName={sourceName}
              includeCouplesPicks={includeCouplesPicks}
              renderListingEntry={renderListingEntry}
              currencyCode={currencyCode}
              navigatorLanguage={navigatorLanguage}
              dynamicPriceMax={dynamicPriceMax}
              showFundSavingsSort={showFundSavingsSort}
              accentColor={scenarioConfig.accentColor}
              listingsPanelWidth={scenarioConfig.listingsPanelWidth}
              featuredPlacement={
                featuredListings.length > 0
                  ? {
                      insertAfterIndex: featuredInsertAfterIndex,
                      content: (
                        <FeaturedPlacements
                          listings={featuredListings}
                          variant={featuredPlacementVariant}
                          state={state}
                          noOfNights={noOfNights}
                          sourceName={sourceName}
                          // The searched-hotel hero above already reports position 1.
                          positionOffset={searchedListing ? 1 : 0}
                        />
                      ),
                    }
                  : undefined
              }
              searchedPlacement={
                // Drawn with the hero design. The arm stays `row` in telemetry; the hero reports
                // position 1 and the band's cards follow from 2.
                searchedListing ? (
                  <FeaturedPlacements
                    listings={[searchedListing]}
                    variant="hero"
                    state={state}
                    noOfNights={noOfNights}
                    sourceName={sourceName}
                  />
                ) : undefined
              }
              isShowingOutsideViewport={isShowingOutsideViewport}
              listingsBanner={renderListingsBanner?.()}
              desktopListingsHeader={desktopListingsHeader}
              hasCompactPadding={scenarioConfig.isFilterPanelCollapsible}
            />
            {hasMoreListingsToShow && !hasApiError && <ShowMoreButton onClick={showNextPageOfListings} />}
          </>
        ) : (
          <>
            {/* The title row holds the filter toggle and the currency picker, so it stays when nothing matches. */}
            {scenarioConfig.isFilterPanelCollapsible && !isMobileOrTablet && (
              <Box
                // The list's desktop top padding, so the row does not move when nothing matches.
                paddingTop={LISTINGS_PANEL_COMPACT_DESKTOP_PADDING_Y}
                paddingLeft={6}
                paddingRight={8}
                width={scenarioConfig.listingsPanelWidth ?? DEFAULT_PANEL_WIDTH}
                maxWidth="100%"
              >
                <DeskTopListingsHeader
                  sortOption={sortOption}
                  onSortChange={updateSort}
                  includeFundSavings={showFundSavingsSort}
                  accentColor={scenarioConfig.accentColor}
                  includeCouplesPicks={includeCouplesPicks}
                  {...desktopListingsHeader}
                />
              </Box>
            )}
            {/* The list's own padding is gone here, so the banner brings the same edges. */}
            {renderListingsBanner?.({
              paddingTop: { _: 6, sm2: scenarioConfig.isFilterPanelCollapsible ? LISTINGS_PANEL_COMPACT_DESKTOP_PADDING_Y : LISTINGS_PANEL_DESKTOP_PADDING_Y },
              paddingLeft: 6,
              paddingRight: { _: 6, sm2: 8 },
              width: { _: 'auto', md: scenarioConfig.listingsPanelWidth ?? DEFAULT_PANEL_WIDTH },
              maxWidth: '100%',
            })}
            <EmptyState
              onReset={handleResetFilters}
              showResetButton={!!cachedListings?.length}
            />
          </>
        )}
      </SkeletonGroup>
    ),
    [
      isFetchingListings,
      isMobileOrTablet,
      hidePanel,
      listingsContainerRef,
      paginatedListings,
      hasApiError,
      state,
      sortOption,
      updateSort,
      noOfNights,
      filters,
      updateFilters,
      isFetchingListingPrices,
      hasMoreListingsToShow,
      showNextPageOfListings,
      handleResetFilters,
      cachedListings?.length,
      sourceName,
      includeCouplesPicks,
      renderListingEntry,
      renderListingsHeader,
      renderListingsBanner,
      currencyCode,
      navigatorLanguage,
      dynamicPriceMax,
      showFundSavingsSort,
      scenarioConfig.accentColor,
      scenarioConfig.listingsPanelWidth,
      renderListingEntrySkeleton,
      featuredListings,
      searchedListing,
      featuredInsertAfterIndex,
      featuredPlacementVariant,
      isShowingOutsideViewport,
      desktopListingsHeader,
      scenarioConfig.isFilterPanelCollapsible,
    ],
  );

  const mapHotels = isFetchingListings ? [] : renderedListings;

  // Defer mounting the Map component (and its Google Maps SDK load) in desktop compact
  // mode until the user clicks "Show on Map". On mobile the map is always behind the
  // drawer so we mount it immediately to avoid a blank area when the drawer is swiped down.
  // See TRAV-2638 for performance analysis.
  const shouldDeferMap = isCompactMapMode && !shouldShowFullMap;

  const hasValidMapLocation = state.circularRegionSearch && isValidCoordinates({ latitude: state.circularRegionSearch.latitude, longitude: state.circularRegionSearch.longitude });

  // An external onMarkerClick means the caller (e.g. admin ExploreArea) owns the marker-click UX,
  // so the built-in mobile pan for SingleHotelView shouldn't run.
  const isCallerOwnedMarkerClick = !!onMarkerClick;

  const mapContent =
    hasValidMapLocation && !shouldDeferMap ? (
      <Map
        location={{ latlng: { lat: state.circularRegionSearch!.latitude, lng: state.circularRegionSearch!.longitude } }}
        hotels={mapHotels}
        isFetchingHotels={isFetchingListings}
        onChangeLocation={onChangeLocation}
        onChangeMapViewPort={onChangeMapViewPort}
        onClickMarker={onClickMarker}
        isCouplePick={shouldUseCouplePickMapMode}
        onToggleLeftPanel={isMapOnlyMode ? undefined : handleOnTogglePanel}
        setReCenterMap={setReCenterMap}
        selectedMarker={selectedHotel?.joyPlaceId || ''}
        reCenterMap={reCenterMap}
        hidePanel={hidePanel}
        mapMarkers={isFetchingListings ? [] : mapMarkers}
        venue={state.venue}
        searchLocation={searchLocationForMap}
        shouldShowVenueMapPin={shouldShowVenueMapPin}
        skipAutoFitAfterUserInteraction={!shouldUseCouplePickMapMode}
        onUserMapInteraction={markUserMovedMap}
        searchResetToken={searchIdentity}
        searchCtaTopOffset={scenarioConfig.searchCtaTopOffset}
        skipMobileMarkerPan={isCallerOwnedMarkerClick}
        markerColor={scenarioConfig.markerColor}
        markerPriceLabelFor={markerPriceLabelFor}
      />
    ) : null;

  if (isMapOnlyMode) {
    return (
      <FeaturedPlacementTelemetryProvider value={featuredPlacementTelemetry}>
        <Box
          position="relative"
          width="100%"
          height="100%"
        >
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            zIndex="docked"
          >
            <ProgressiveLoader
              isLoading={isCombinedLoading}
              accentColor={scenarioConfig.accentColor}
            />
          </Box>
          <AccommodationMapListSync>
            {mapContent}
            {isFetchingListings && !state.circularRegionSearch && <MapLoader />}
          </AccommodationMapListSync>
        </Box>
      </FeaturedPlacementTelemetryProvider>
    );
  }

  return (
    <FeaturedPlacementTelemetryProvider value={featuredPlacementTelemetry}>
      <Container>
        <Box ref={headerRef}>
          {isMobileOrTablet || !isDesktopHeaderEmpty(scenarioConfig) ? (
            <AccommodationsConsoleHeader
              state={state}
              topBar={topBar}
              onClose={onClose}
              eventHandle={eventHandle}
              onSearchOpen={onSearchOpen}
              onFilterPanelOpen={onFilterPanelOpen}
              handleFormSubmit={handleSearchFormSubmit}
              searchCtaColor={scenarioConfig.accentColor}
              isSearchBarHidden={scenarioConfig.presetSearchDates !== undefined}
              isDesktopCurrencySelectorHidden={!!scenarioConfig.isFilterPanelCollapsible}
            />
          ) : null}
        </Box>
        {!isMobileOrTablet && (
          <ProgressiveLoader
            isLoading={isCombinedLoading}
            accentColor={scenarioConfig.accentColor}
          />
        )}
        {/* Main Content */}
        {isMobileOrTablet && hasApiError && <ErrorState />}
        {isMobileOrTablet && !hasApiError && (
          <AccommodationMapListSync>
            <MobileMapDrawerLayout
              headerHeight={headerSize.height}
              listingsPanelContent={listingsPanelContent}
              drawerBackgroundColor={scenarioConfig.pageBackgroundColor}
              isDrawerSizedToParent={!!scenarioConfig.fillParent}
            >
              {mapContent}
              {isFetchingListings && !state.circularRegionSearch && <MapLoader />}
            </MobileMapDrawerLayout>
            <BottomSheet
              isOpen={isFilterPanelOpen}
              onClose={onFilterPanelClose}
            >
              {filtersPanelContent}
            </BottomSheet>
            <BottomSheet
              isOpen={isSearchOpen}
              onClose={onSearchClose}
            >
              <MobileSearchComponent
                onClose={onSearchClose}
                accentColor={scenarioConfig.accentColor}
                state={state}
                handleFormSubmit={handleSearchFormSubmit}
                searchForm={topBar.searchForm}
              />
            </BottomSheet>
            {selectedHotel && (
              <SingleHotelView
                onClose={() => setSelectedHotel(null)}
                listings={renderedListings}
                selectedHotelId={selectedHotel.joyPlaceId}
                state={state}
                noOfNights={noOfNights}
                sourceName={sourceName}
                renderListingEntry={renderListingEntry}
              />
            )}
          </AccommodationMapListSync>
        )}
        {!isMobileOrTablet && (
          <Content>
            {isFetchingListings && isInitialLoad ? (
              <Flex
                width="100%"
                height={'100%'}
                justifyContent="center"
                alignItems="center"
              >
                <AccommodationsConsoleLoader />
              </Flex>
            ) : (
              <AccommodationMapListSync>
                {isCompactMapMode ? (
                  <Flex
                    width="100%"
                    height="100%"
                    overflowY="auto"
                    justifyContent={shouldShowFullMap ? 'flex-start' : 'center'}
                    alignItems="flex-start"
                    transition="all 0.3s ease-in-out"
                    position="relative"
                  >
                    <Box
                      position="sticky"
                      top={0}
                      alignSelf="flex-start"
                      height="fit-content"
                      maxHeight="100%"
                      overflowY="auto"
                      overflowX="hidden"
                      zIndex={1}
                    >
                      {filtersPanelContent}
                    </Box>
                    {listingsPanelContent}
                    {shouldShowFullMap && mapContent ? (
                      <Box
                        flex="1"
                        height="100%"
                        position="sticky"
                        top={0}
                        alignSelf="flex-start"
                        transition="all 0.3s ease-in-out"
                        style={{
                          animation: 'slideIn 0.3s ease-in-out',
                        }}
                      >
                        <Box
                          position="absolute"
                          top={7}
                          right={7}
                          zIndex="docked"
                        >
                          <ListButton onClick={handleShowList} />
                        </Box>
                        {mapContent}
                      </Box>
                    ) : null}
                  </Flex>
                ) : (
                  <>
                    {isFilterPanelToggleShown ? (
                      <CollapsibleFiltersPanel isOpen={isCollapsibleFilterPanelOpen}>{filtersPanelContent}</CollapsibleFiltersPanel>
                    ) : (
                      filtersPanelContent
                    )}
                    {listingsPanelContent}
                    {mapContent}
                  </>
                )}
              </AccommodationMapListSync>
            )}
          </Content>
        )}
      </Container>
    </FeaturedPlacementTelemetryProvider>
  );
};

const Container = ({ children }: { children?: React.ReactNode }) => {
  return <>{children}</>;
};

const Content = ({ children }: { children: React.ReactNode }) => {
  const isMobileOrTablet = useIsMobileOrTablet();
  return (
    <Flex
      overflow="auto"
      flex={1}
      // flex:1 fills remaining space after the header inside the SkeletonGroup flex column;
      // minHeight:0 on desktop overrides the flex default (content-sized) so overflow scrolling works correctly
      minHeight={isMobileOrTablet ? '400px' : 0}
    >
      {children}
    </Flex>
  );
};

export const AccommodationsConsole = ({
  eventHandle,
  eventName,
  onClose,
  selectedHotelId,
  scenarioConfig,
  renderListingEntry,
  renderListingsHeader,
  renderListingsBanner,
  renderListingEntrySkeleton,
  renderFiltersHeader,
  onMarkerClick,
  onListingsSettled,
  markerClickFrom,
}: AccommodationsConsoleProps) => {
  const scenarioFlags = resolveScenarioFlags(scenarioConfig.variant);
  const { shouldShowAllAccommodations, showJoyTravelBenefitsFilter } = scenarioFlags;
  // Read once here and passed down: it decides the sort, and `Root` uses the same value for the
  // venue pin and the map bounds.
  const isHoneymoonFundSearch = useIsHoneymoonFundSearch();
  const defaultSortOption = resolveDefaultSortOption({ flags: scenarioFlags, cashFund: scenarioConfig.cashFund, isHoneymoonFundSearch });
  const { state, loading, handleOnChangeLocation, handleHeaderSearchFormSubmit, isInitialLoad, isShowingAllCouplesPicks } = useAccommodationsConsoleSearchParamsState({
    eventHandle,
    shouldShowAllAccommodations,
    defaultSortOption,
    presetSearchDates: scenarioConfig.presetSearchDates,
  });
  const isMobileOrTablet = useIsMobileOrTablet();

  const { pageViewed } = useAccommodationsConsoleTelemetry();

  useMount(() => {
    pageViewed(scenarioConfig.variant);
  });

  return (
    <JoyKitThemeProvider>
      <HotelOfferDiagnosticsProvider>
        <SkeletonGroup
          isReady={!loading || !!state}
          flexDirection="column"
          width={'100%'}
          height={scenarioConfig.fillParent ? '100%' : '100dvh'}
          maxWidth="100%"
          overflow={scenarioConfig.fillParent ? 'hidden' : 'auto'}
          display="flex"
          // NB: use `background` not `backgroundColor` — SkeletonGroup destructures `backgroundColor`
          // for its shimmer base, so it never reaches the content root.
          background={scenarioConfig.pageBackgroundColor}
          placeholder={
            <Container>
              <AccommodationsConsoleHeader.Skeleton />
              <Content>
                <FiltersPanel.Skeleton showJoyTravelBenefitsFilter={showJoyTravelBenefitsFilter} />
                <ListingsPanel.Skeleton
                  isMobileOrTablet={isMobileOrTablet}
                  listingsPanelWidth={scenarioConfig.listingsPanelWidth}
                  renderListingEntrySkeleton={renderListingEntrySkeleton}
                  hasCompactPadding={scenarioConfig.isFilterPanelCollapsible}
                />
                <AccommodationsConsoleMapSkeleton mapLayoutMode={scenarioConfig.mapLayoutMode} />
              </Content>
            </Container>
          }
        >
          {state && (
            <AccommodationsConsoleErrorBoundary>
              <Root
                state={state}
                eventHandle={eventHandle}
                eventName={eventName}
                onClose={onClose}
                scenarioConfig={scenarioConfig}
                scenarioFlags={scenarioFlags}
                defaultSortOption={defaultSortOption}
                isHoneymoonFundSearch={isHoneymoonFundSearch}
                onChangeLocation={handleOnChangeLocation}
                onHeaderSearchSubmit={handleHeaderSearchFormSubmit}
                selectedHotelId={selectedHotelId}
                isInitialLoad={isInitialLoad}
                isShowingAllCouplesPicks={isShowingAllCouplesPicks}
                renderListingEntry={renderListingEntry}
                renderListingsHeader={renderListingsHeader}
                renderListingsBanner={renderListingsBanner}
                renderListingEntrySkeleton={renderListingEntrySkeleton}
                renderFiltersHeader={renderFiltersHeader}
                onMarkerClick={onMarkerClick}
                onListingsSettled={onListingsSettled}
                markerClickFrom={markerClickFrom}
              />
            </AccommodationsConsoleErrorBoundary>
          )}
        </SkeletonGroup>
      </HotelOfferDiagnosticsProvider>
    </JoyKitThemeProvider>
  );
};

export const AccommodationsConsoleWithHealth = wrapWithAccommodationsConsoleProvider(AccommodationsConsole);
